<template>
    <BaseModal
        :title="isEditing ? 'Edit User' : 'Create User'"
        :loading="loading"
        :submit-text="isEditing ? 'Update User' : 'Create User'"
        max-width="2xl"
        @close="closeModal"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
                <label
                    for="name"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Name
                </label>
                <input
                    id="name"
                    v-model="formData.name"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    :class="{ 'border-danger-500': errors.name }"
                />
                <p
                    v-if="errors.name && errors.name.length > 0"
                    class="mt-1 text-sm text-danger-600 dark:text-danger-400"
                >
                    {{ errors.name[0] }}
                </p>
            </div>

            <div>
                <label
                    for="email"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Email
                </label>
                <input
                    id="email"
                    v-model="formData.email"
                    type="email"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    :class="{ 'border-danger-500': errors.email }"
                />
                <p
                    v-if="errors.email && errors.email.length > 0"
                    class="mt-1 text-sm text-danger-600 dark:text-danger-400"
                >
                    {{ errors.email[0] }}
                </p>
            </div>

            <div>
                <label
                    for="password"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Password
                    <span v-if="!isEditing" class="text-xs text-gray-500"
                        >(required)</span
                    >
                </label>
                <input
                    id="password"
                    v-model="formData.password"
                    type="password"
                    :required="!isEditing"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    :class="{ 'border-danger-500': errors.password }"
                />
                <p
                    v-if="errors.password && errors.password.length > 0"
                    class="mt-1 text-sm text-danger-600 dark:text-danger-400"
                >
                    {{ errors.password[0] }}
                </p>
            </div>

            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Status
                </label>
                <div class="flex items-center space-x-3">
                    <span
                        class="text-sm text-gray-600 dark:text-gray-400"
                        :class="{
                            'text-gray-900 dark:text-white font-medium':
                                !formData.status,
                        }"
                    >
                        Inactive
                    </span>
                    <button
                        type="button"
                        @click="formData.status = !formData.status"
                        class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        :class="
                            formData.status
                                ? 'bg-primary-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                        "
                    >
                        <span
                            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                            :class="
                                formData.status
                                    ? 'translate-x-5'
                                    : 'translate-x-0'
                            "
                        />
                    </button>
                    <span
                        class="text-sm text-gray-600 dark:text-gray-400"
                        :class="{
                            'text-gray-900 dark:text-white font-medium':
                                formData.status,
                        }"
                    >
                        Active
                    </span>
                </div>
            </div>

            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Roles
                </label>
                <div
                    class="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-48 overflow-y-auto p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700"
                >
                    <label
                        v-for="role in availableRoles"
                        :key="role.id"
                        class="flex items-center space-x-2 p-2 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-primary-50 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                    >
                        <input
                            type="checkbox"
                            :value="role.id"
                            v-model="formData.role_ids"
                            class="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div class="flex-1">
                            <div
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ role.name }}
                            </div>
                            <div
                                class="text-xs text-gray-500 dark:text-gray-400"
                            >
                                {{ role.guard_name }}
                            </div>
                        </div>
                    </label>
                </div>
                <p
                    v-if="!isEditing && formData.role_ids.length === 0"
                    class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                >
                    Tip: Select at least one role (administrator or tu)
                </p>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import type { User, CreateUserRequest, Role } from "@/api/user";
import { useUserStore } from "@/stores/userStore";
import BaseModal from "@/components/BaseModal.vue";
import roleApi from "@/api/role";

interface Props {
    user?: User | null;
    isEditing?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    isEditing: false,
});

const emit = defineEmits<{
    close: [];
    saved: [];
}>();

const userStore = useUserStore();

const isEditing = computed(() => props.isEditing);

const loading = ref(false);
const allRoles = ref<Role[]>([]);
const formData = ref<CreateUserRequest>({
    name: "",
    email: "",
    password: "",
    status: true,
    role_ids: [],
});
const errors = ref<Record<string, string[]>>({});

const resetErrors = () => {
    errors.value = {};
};

const availableRoles = computed(() => {
    return allRoles.value.filter((role) =>
        ["administrator", "tu"].includes(role.name),
    );
});

const fetchRoles = async () => {
    try {
        const response = await roleApi.getRoles({ per_page: 100 });
        allRoles.value = response.data?.items || [];
    } catch (error) {
        console.error("Failed to fetch roles:", error);
    }
};

watch(
    () => props.user,
    (newUser) => {
        resetErrors();
        if (newUser) {
            formData.value = {
                name: newUser.name || "",
                email: newUser.email || "",
                password: "",
                status: newUser.status ?? true,
                role_ids: newUser.roles?.map((r: Role) => r.id) || [],
            };
        } else {
            formData.value = {
                name: "",
                email: "",
                password: "",
                status: true,
                role_ids: [],
            };
        }
    },
    { immediate: true },
);

onMounted(() => {
    fetchRoles();
});

const title = computed(() => {
    return props.isEditing ? "Edit User" : "Create User";
});

const submitButtonText = computed(() => {
    return props.isEditing ? "Update User" : "Create User";
});

const closeModal = () => {
    emit("close");
};

const validateForm = (): boolean => {
    const newErrors: Record<string, string[]> = {};

    if (!formData.value.name.trim()) {
        newErrors.name = ["Name is required"];
    } else if (formData.value.name.length < 2) {
        newErrors.name = ["Name must be at least 2 characters"];
    }

    if (!formData.value.email.trim()) {
        newErrors.email = ["Email is required"];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
        newErrors.email = ["Please enter a valid email address"];
    }

    if (!props.isEditing && !formData.value.password) {
        newErrors.password = ["Password is required"];
    } else if (formData.value.password && formData.value.password.length < 8) {
        newErrors.password = ["Password must be at least 8 characters"];
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;
    resetErrors();
    try {
        const submitData: any = { ...formData.value };
        if (props.isEditing && !submitData.password) {
            delete submitData.password;
        }

        if (props.isEditing && props.user) {
            await userStore.updateUser(props.user.id, submitData);
        } else {
            await userStore.createUser(submitData);
        }
        formData.value = {
            name: "",
            email: "",
            password: "",
            status: true,
            role_ids: [],
        };
        errors.value = {};
        emit("saved");
    } catch (error: any) {
        console.error("Save error:", error);

        if (error.response?.status === 422) {
            errors.value = error.response.data.errors;
            return;
        }
        throw error;
    } finally {
        loading.value = false;
    }
};
</script>
